import { useDispatch, useSelector } from 'react-redux'
import { WidgetListItems } from '../../util/sidebar'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    CalculateBasketAnalyzeProfit,
    CalculateProfit,
    getCurentOrderPricesFromData,
    updateDataWithBreakevenPoints,
    updateIvFromAtmIv,
    generatePlChartData,
    getCurentOrderLtpPricesFromData,
    getCurrentOrderLtpPriceFromData
} from '../../util/tradepanel'
import { Box, Paper } from '@mui/material'
import { selectSnapshot } from '../../selectors/security'
import commonActions from '../../actions/common.actions'
import { OrderDataPrices, OrdersDataType, setOrderDataFunction, setBasketId, BasketAnalyzeDetails, OptionOrderDataType } from '../../reducer/orders.reducer'
import { selectOptionsChainUnderlyingPrices, selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { selectExpandedRows, selectOptionChainTimeInterval} from '../../selectors/home'
import PayoffWrapper, { CustomProfitType, PayoffDataType } from '../../components/payoffChart/PayoffWrapper'
import { optionsTradesDataType, optionTradeDataType } from '../typings/options.typings'
import { log10 } from 'mathjs'
import { v4 as uuid } from 'uuid'
import { selectBasketAnalyzeDetails, selectOrdersData } from '../../selectors/orders'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { useStyles } from './styles'
import PayoffButtons from '../../components/payoffChart/payoffButtons'
import { fetchOptionChainV2, fetchOptionChainV2Initial, findSimulatorSquareOffOrder } from '../../util/options'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { GetOptionChainRequestV2 } from '../../proto/generated/candle-data-depot-option-chain'
import { strikeValues } from '../../util/tradePage'
import { debounce } from 'throttle-debounce'
import { OptionChainViewKey } from '../../assets/constants'
import { setSimulatorOrderDataFunction } from '../../reducer/simulator.reducer'
import { selectSimulatorDate } from '../../selectors/simulator'
import { ActionType, useUndoRedoContext } from '../../components/UndoRedoFunctonalities/UndoRedo.context'
import { Security } from '../../proto/generated/security'
import { Snapshot } from '../../reducer/security.reducer'
import { selectTradingCalendarDataByExchange } from '../../selectors/chart'

export type OiDataTypeBars = {
    [key: string]: number | undefined | boolean
    strike: number | undefined
    callOi: number | undefined
    putOi: number | undefined
    callDelta: number | undefined
    putDelta: number | undefined
    profit: number
    expectedProfit: number
    duplicateStrike: boolean
    axisStrike?: boolean
}

export const greenFull = '#22AB94' //'rgb(34, 171, 148)'
export const greenHalf = 'rgba(34, 171, 148, 0.5)'
export const greenLight = 'rgba(34, 171, 148, 0.3)'
export const greenArea = 'rgba(24, 128, 110, 0.1)'
export const redHalf = 'rgba(242, 54, 69, 0.5)'
export const redLight = 'rgba(242, 54, 69, 0.3)'
export const redFull = '#F23645' //'rgb(242, 54, 69)'
export const redArea = 'rgba(132, 27, 36, 0.1)'
export const blueFull = '#006CE6'
export const background = 'none'
const lightgray = 'rgba(111, 111, 111, 0.5)'

type Props = {
    readOnly?: boolean
    optionChainViewKey: OptionChainViewKey
}

export const defaultMargin = { top: 30, right: 30, bottom: 15, left: 20 }

export type SdDataType = {
    label: string
    time: number
}

const PlChart: React.FC<Props> = ({ readOnly = false, optionChainViewKey }: Props) => {
    const dispatch = useDispatch()
    const { push } = useUndoRedoContext()
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const {primaryOrUnderlying: security, primaryOrUnderlyingToken: underlyingToken} = useSelectPrimaryOrUnderlyingSecurity()
    const securitySnapshot = useSelector(selectSnapshot(underlyingToken))
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const optionsUnderlyingPrices = useSelector(selectOptionsChainUnderlyingPrices(underlyingToken))
    const orderData = useSelector(selectOrdersData)
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const basketAnalyzeDetails: BasketAnalyzeDetails | undefined = useSelector(selectBasketAnalyzeDetails)
    const { orderState, basketId } = orderData
    const [plChartData, setPlChartData] = useState<OiDataTypeBars[]>([])
    const [data, setData] = useState<OiDataTypeBars[]>([...plChartData])
    const [parentSqOffdata, setParentSqOffData] = useState<OiDataTypeBars[]>([...plChartData])
    const [markerPositions, setMarkerPositions] = useState<number[]>([])
    const markerPositionsRef = useRef<number[]>([])
    const [targetDaysFromToday, setTargetDaysFromToday] = useState<number>(0)
    const [numberOfDaysToExpire, setNumberOfDaysToExpire] = useState<number>(0)
    const [numberOfDaysToExpireArray, setNumberOfDaysToExpireArray] = useState<number[]>([])
    const priceMultiplier = useMemo(() => (security?.priceMultiplier ?? 100), [security?.priceMultiplier])
    const livePrice = useMemo(() => securitySnapshot?.lastTradedPrice / Number(priceMultiplier.toFixed(log10(priceMultiplier))), [securitySnapshot, priceMultiplier])
    const historicalPrice = useMemo(() => (optionsUnderlyingPrices?.underlyingCurrentPrice??0) / Number(priceMultiplier.toFixed(log10(priceMultiplier))), [optionsUnderlyingPrices, priceMultiplier])
    const price = useMemo(() => (optionChainViewKey === OptionChainViewKey.LIVE ? livePrice : historicalPrice), [optionChainViewKey, livePrice, historicalPrice])
    const [ivMultiplier, setIvMultiplier] = useState<number>(1)
    const [breakEven, setBreakEven] = useState<number[]>([])
    const profitThreshold = basketAnalyzeDetails?.executedProfit ?? 0
    const [basePriceForProfitCalc, setBasePriceForProfitCalc] = useState<number>(price)
    const [spotPriceForProfitCalc, setSpotPriceForProfitCalc] = useState<number>(price)
    const [priceThreshold, setPriceThreshold] = useState<number>(0)
    const timebucket = useSelector(selectOptionChainTimeInterval(optionChainViewKey, 'normal'))
    const maxStrikesLoaded = useRef<Record<number, number>>({})
    const calendarEvents = useSelector(selectTradingCalendarDataByExchange(security?.exchange))
    const holidays = useMemo(() => {
        return new Set(calendarEvents?.holidays?.map((e) => e.date))
    }, [calendarEvents])

    useEffect(()=>{
        if(optionChainViewKey === OptionChainViewKey.HISTORICAL) {
            setBasePriceForProfitCalc(historicalPrice)
            setSpotPriceForProfitCalc(historicalPrice)
        }
    }, [historicalPrice, optionChainViewKey])
    
    const { classes } = useStyles()
    const {
        orders = [],
        analyzePlusAllOrders = [],
        analyzePlusAllOrderPrices = [],
        allOrders,
        allOrdersPrices,
        analyzeOrders = [],
        ordersPrices = [],
        parentSquareOffOrders = [],
        distinct: displayBasketProfit
    } = useCombineBasketAnalyzeAndLegOrders()
    const showPlGraph = useMemo(() => (Boolean(allOrders.length)||Boolean(analyzeOrders.length)), [allOrders.length, analyzeOrders.length])

    const simulatorOrders = useMemo(()=>{
        return orders.filter(o=>{
            const sqOrder = findSimulatorSquareOffOrder(o, orders, simulatorTimestamp)
            return !sqOrder&&!Boolean(o.sqOffWithOrderId)&&((o.timeStamp??0)<=simulatorTimestamp)
        })
    }, [orders, simulatorTimestamp])

    const [ordersForCalculation, orderPricesForCalculation] = useMemo(()=>{
        if(optionChainViewKey===OptionChainViewKey.LIVE) {
            return [orders, ordersPrices]
        }
        const newOrders: OrdersDataType[] = []
        const newOrderPrices: OrderDataPrices[] = []
        orders.forEach((order, i)=>{
            if((order.timeStamp??0) <= simulatorTimestamp) {
                newOrders.push(order)
                newOrderPrices.push(ordersPrices[i])
            }
        })
        return [newOrders, newOrderPrices]
    }, [optionChainViewKey, orders, ordersPrices, simulatorTimestamp])

    useEffect(() => {
        if (orders.length && optionsTradesData) {
            let orderExpiryIndexes: number[] = []
            orders.forEach((o, i) => {
                const ind = optionsTradesData.findIndex((oe) => oe.expiration.expiry?.expiry === o.security?.expiry?.expiry)
                orderExpiryIndexes.push(ind)
            })
            let nearestExpiryOrderIndex = 0
            let nearestExpiryIndex = orderExpiryIndexes[0]
            orderExpiryIndexes.forEach((orderExpiryIndex, j) => {
                if (orderExpiryIndex <= nearestExpiryIndex) {
                    nearestExpiryIndex = orderExpiryIndex
                    nearestExpiryOrderIndex = j
                }
            })
            const nearestExpiry = optionsTradesData.find((o) => o.expiration.expiry?.expiry === orders[nearestExpiryOrderIndex].security?.expiry?.expiry)
            const numberOfDaysString = nearestExpiry?.numberOfDaysToExpiry
            const numberOfDays = Number(numberOfDaysString?.substring(0, numberOfDaysString.length - 1))
            setNumberOfDaysToExpire(numberOfDays)
            const array: number[] = []
            for (let i = 0; i <= numberOfDays; i++) {
                const current = optionChainViewKey === OptionChainViewKey.HISTORICAL ? new Date(simulatorTimestamp) : new Date()
                current.setDate(current.getDate() + i)
                const yyyymmdd = [
                    current.getFullYear(),
                    String(current.getMonth() + 1).padStart(2, '0'),
                    String(current.getDate()).padStart(2, '0')
                ].join('')
                const isWeekend = current.getDay() === 0 || current.getDay() === 6;
                if(!holidays.has(yyyymmdd) && !isWeekend) {
                    array.push(i)
                }
            }
            setNumberOfDaysToExpireArray(array)
        }
    }, [optionsTradesData, orders, simulatorTimestamp])

    const setMarkerPositionsFun = useCallback((markerPositionsNew: number[]) => {
        setMarkerPositions(markerPositionsNew)
        markerPositionsRef.current = markerPositionsNew
    }, [])

    useEffect(() => {
        if ((optionChainViewKey===OptionChainViewKey.LIVE ? analyzePlusAllOrders : simulatorOrders).length) {
            const newPositions: number[] = [];
            (optionChainViewKey===OptionChainViewKey.LIVE ? analyzePlusAllOrders : simulatorOrders).forEach((o, i) => {
                newPositions.push(o.contractType === 'FUTURE' ? (Number(analyzePlusAllOrderPrices[i].price)): (o.strike / priceMultiplier))
            })
            setMarkerPositionsFun(newPositions)
        }else{
            setMarkerPositionsFun([])
        }
    }, [analyzePlusAllOrderPrices, analyzePlusAllOrders, simulatorOrders])

    const calculateProfits = useCallback((data: OiDataTypeBars[], parentSqOffdata: OiDataTypeBars[]) => {
        if (orders.length > 0 && security && optionsTradesData) {
            const parentSquareOffOrdersPrices: OrderDataPrices[] =
                parentSquareOffOrders.map((o) => {
                    return {
                        price: ((o.orderDetails?.price ?? 0) / (o.security?.priceMultiplier ?? 100)).toFixed(2) ?? '',
                        topAsk: 0,
                        topBid: 0,
                        mid: 0,
                        visibility: true
                    }
                }) ?? ([] as OrderDataPrices[])
            CalculateProfit(
                data,
                updateIvFromAtmIv([...ordersForCalculation, ...parentSquareOffOrders], optionsTradesData, ivMultiplier),
                [...orderPricesForCalculation, ...parentSquareOffOrdersPrices],
                security,
                setData,
                targetDaysFromToday,
                optionChainViewKey===OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp
            )
            CalculateProfit(
                parentSqOffdata,
                updateIvFromAtmIv([...parentSquareOffOrders], optionsTradesData, ivMultiplier),
                parentSquareOffOrdersPrices,
                security,
                setParentSqOffData,
                targetDaysFromToday,
                optionChainViewKey===OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp
            )
        }
    }, [orders, ordersForCalculation, security, optionsTradesData, parentSquareOffOrders, ivMultiplier, orderPricesForCalculation, targetDaysFromToday, optionChainViewKey, simulatorTimestamp])

    useEffect(() => {
        if(data.length){
            calculateProfits(data, parentSqOffdata)
        }
    }, [orders, parentSquareOffOrders, ordersPrices, targetDaysFromToday, security, ivMultiplier, optionsTradesData])

    useEffect(() => {
        const newData: OiDataTypeBars[] = JSON.parse(JSON.stringify(plChartData))
        const newParentSqOffData: OiDataTypeBars[] = [...JSON.parse(JSON.stringify(plChartData))]
        setData(newData)
        setParentSqOffData(newParentSqOffData)
        calculateProfits(newData, newParentSqOffData)
    }, [plChartData])

    const sdData = useMemo(() => {
        if (!optionsTradesData || optionsTradesData?.length === 0) return []
        const index = expandedRows.length > 0 ? Number(expandedRows[0]) : 0

        const strike = optionsTradesData[index].trades.find((t) => t.strikeCol.strike === optionsTradesData[index].atmStrike)
        const loop = [-2, -1, 1, 2]
        const newSdData: SdDataType[] = []
        if (strike) {
            const sdPercent = optionsTradesData[index].standardDeviation
            const osdTime = optionsTradesData[index].atmStrike / priceMultiplier
            loop.forEach((num) => {
                newSdData.push({
                    label: `${num} SD`,
                    time: Number((osdTime * (1 + 0.01 * sdPercent * num)).toFixed(0))
                })
            })
        }
        newSdData.push({
            // label: 'Spot: ' + (basePriceForProfitCalc).toFixed(2),
            label: 'Spot',
            time: basePriceForProfitCalc
        })
        return newSdData
    }, [basePriceForProfitCalc, expandedRows, optionsTradesData, priceMultiplier])

    const handleStrikeChange = useCallback(
        async (i: number, strike: number, orders: OrdersDataType[], ordersPrices: OrderDataPrices[]) => {
            if(!optionsTradesData) return
            const newOrders = [...orders]
            if (newOrders[i].readonly) return newOrders
            let newOptionsTradesData = [...optionsTradesData]
            const r = newOptionsTradesData.findIndex((o) => o.expiration.expiry?.expiry === newOrders[i].security?.expiry?.expiry)
            let modified = false;
            if (newOptionsTradesData && security && newOptionsTradesData?.[r]?.trades?.length <= 0) {
                const {optionsData} = await fetchOptionChainV2Initial(security, dispatch, true, [Number(optionsTradesData[r].expirySeconds)], newOptionsTradesData)
                newOptionsTradesData = optionsData
            }
            const strikesMenu = newOptionsTradesData[r].trades.map((t) => t.strikeCol.strike)
            const index = strikesMenu.findIndex((t) => t === strike)
            if(index !== undefined && index >= 0){
                if(newOrders[i].contractType === 'OPTION'){
                    const newStrike = strikesMenu[index]
                    const data = optionsTradesData.find((o) => o.expiration.expiry?.expiry === newOrders[i].security?.expiry?.expiry)?.trades.find((t) => t.strikeCol.strike === newStrike)
                    if (data) {
                        const option = (newOrders[i] as OptionOrderDataType).callput === 'call' ? data?.callOption : data?.putOption
                        newOrders[i] = {
                            ...newOrders[i],
                            data: data,
                            strike: newStrike,
                            security: option?.security
                        } as OrdersDataType

                        modified = true

                    }
                }else if (newOrders[i].contractType === 'FUTURE'){
                    newOrders[i] = {
                        ...newOrders[i]
                    }
                    modified = true
                }
            }
            if(modified){
                if(optionChainViewKey===OptionChainViewKey.LIVE) {
                    getCurentOrderPricesFromData(newOrders.filter((o) => !o.readonly), optionsTradesData, underlyingToken, []).then((orderPrices) => {
                        setOrderDataFunction({orders: newOrders.filter((o) => !o.readonly), orderPrices})
                    })
                } else {
                    const newOrderPrices = getCurentOrderLtpPricesFromData(orders, optionsTradesData, underlyingToken)
                    push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrderPrices }}, 'simulator orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                    setSimulatorOrderDataFunction({orders: newOrders, orderPrices: newOrderPrices})
                }
            }
            return newOrders
        },
        [optionsTradesData, security, dispatch, optionChainViewKey, underlyingToken, push, allOrders, allOrdersPrices]
    )

    const generatePlChartChartDataDebouncer = useCallback(debounce(200, async (otd: optionsTradesDataType[] | undefined, ords: OrdersDataType[], ers: string[], security: Security, securitySnapshot: Snapshot) => {
        if(!otd) return
        let newOptionsExpiries = [...(otd ?? [])]
        let index = Infinity
        if (ords.length) {
            index = newOptionsExpiries.findIndex((d) => d.expiration.expiry?.expiry === ords[0].security?.expiry?.expiry)
            ords.forEach((order, i) => {
                if (i > 0) {
                    const newIndex = newOptionsExpiries.findIndex((d) => d.expiration.expiry?.expiry === ords[i].security?.expiry?.expiry)
                    if (newIndex !== -1 && newIndex < index) {
                        index = newIndex
                    }
                }
            })
        }
        if (index === -1 || !isFinite(index)) {
            index = ers.length > 0 ? Number(ers[0]) : 0
        }
        let optionExpiry = newOptionsExpiries.filter((t) => !t.derived)?.[index]
        let data = optionExpiry?.trades
        if(optionChainViewKey===OptionChainViewKey.LIVE && security && (!data || data.length <= strikeValues.maxValue) && !maxStrikesLoaded.current?.[index]){
            const payload: GetOptionChainRequestV2 = {
                security: {
                    code: security?.code,
                    exchange: security?.exchange
                },
                expiries: [optionExpiry?.expiration.expiry?.expiry ?? 0],
                noOfStrikes: -1,
                requestedFields: [],
                timeBucket: timebucket,
                sendFutures: true
            }
            const {optionsData} = await fetchOptionChainV2(payload, dispatch, newOptionsExpiries, 'both', false)
            newOptionsExpiries = optionsData
            maxStrikesLoaded.current[index] = newOptionsExpiries[index].trades.length
            optionExpiry = newOptionsExpiries.filter((t) => !t.derived)?.[index]
            data = optionExpiry?.trades
        }
        setPlChartData(generatePlChartData({data, ltp: securitySnapshot?.lastTradedPrice, priceMultiplier, filterLowValueStrikes: true}))
    }), [dispatch, priceMultiplier, timebucket, optionChainViewKey])

    useEffect(() => {
        generatePlChartChartDataDebouncer(optionsTradesData, orders, expandedRows, security, securitySnapshot)
    }, [optionsTradesData, orders, expandedRows, security, securitySnapshot?.lastTradedPrice])

    const payoffData: PayoffDataType[] = useMemo(() => {
        let pData = data.map((dt) => ({
            ...dt,
            strike: dt.strike !== undefined ? Number((dt.strike).toFixed(0)) : undefined,
            profit: Number.isFinite(dt.profit) ? dt.profit + profitThreshold : dt.profit,
            expectedProfit: Number.isFinite(dt.expectedProfit) ? dt.expectedProfit + profitThreshold : dt.expectedProfit
        }))
        if (!showPlGraph) {
            setBreakEven([])
        } else {
            const { breakeven } = updateDataWithBreakevenPoints(data)
            setBreakEven(breakeven)
            // pData = updatedData.map((dt) =>({
            //     ...dt,
            //     profit: Number.isFinite(dt.profit) ? dt.profit + profitThreshold : dt.profit,
            //     expectedProfit: Number.isFinite(dt.expectedProfit) ? dt.expectedProfit + profitThreshold : dt.expectedProfit,
            // }))
        }
        if (displayBasketProfit && basketAnalyzeDetails && security && optionsTradesData) {
            const setProfitData = (data: PayoffDataType[]) => {
                pData = data
            }
            CalculateBasketAnalyzeProfit(
                pData,
                updateIvFromAtmIv([...basketAnalyzeDetails.positionOrders], optionsTradesData, ivMultiplier),
                [...basketAnalyzeDetails.positionOrderPrices],
                security,
                setProfitData,
                targetDaysFromToday
            )
        }
        return pData
    }, [data, showPlGraph, displayBasketProfit, basketAnalyzeDetails, security, optionsTradesData, profitThreshold, ivMultiplier, targetDaysFromToday])

    const handleAddOptionOrder = useCallback(
        async (data: {type: 'buy' | 'sell', record: optionTradeDataType, callput: 'call' | 'put'}[]) => {
            if (!optionsTradesData) return
            if (optionChainViewKey === OptionChainViewKey.LIVE) {
                const newOrders = [...allOrders].filter((o) => !o.readonly)
                data.forEach(({type, record, callput})=>{
                    if (orderState === 'order') {
                        const exist = allOrders.find((v) => v.data?.key === record?.key && v.type === type && (v as OptionOrderDataType).callput === callput)
                        if (!exist) {
                            const sameSecDifOrder = newOrders.findIndex((v) => v.data?.key === record?.key && (v as OptionOrderDataType).callput === callput)
                            if (sameSecDifOrder >= 0) {
                                newOrders[sameSecDifOrder] = {
                                    ...newOrders[sameSecDifOrder],
                                    type
                                }
                                getCurentOrderPricesFromData(newOrders, optionsTradesData, underlyingToken, []).then((orderPrices) => {
                                    setOrderDataFunction({ orders: newOrders, orderPrices })
                                })
                            } else if (newOrders.length < 8) {
                                const option = callput === 'call' ? record.callOption : record.putOption
                                newOrders.push({
                                    type,
                                    security: option?.security,
                                    data: record,
                                    callput,
                                    quantity: 1,
                                    expiry: record.callOption?.security?.expiry?.expiry ?? 0,
                                    strike: record.strikeCol.strike,
                                    orderDetails: undefined,
                                    uuid: uuid(),
                                    contractType: 'OPTION',
                                    visibility: true
                                })
                                getCurentOrderPricesFromData(newOrders, optionsTradesData, underlyingToken, []).then((orderPrices) => {
                                    setOrderDataFunction({ orders: newOrders, orderPrices })
                                })
                                if (!basketAnalyzeDetails) commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                            }
                            if (basketAnalyzeDetails && basketId !== basketAnalyzeDetails.id) {
                                dispatch(
                                    setBasketId({
                                        basketId: basketAnalyzeDetails?.id ?? uuid()
                                    })
                                )
                            }
                        }
                    } else if (orderState === 'complete') {
                        const newOrders: OrdersDataType[] = []
                        const option = callput === 'call' ? record.callOption : record.putOption
                        newOrders.push({
                            type,
                            security: option?.security,
                            data: record,
                            callput,
                            quantity: 1,
                            expiry: record.callOption?.security?.expiry?.expiry ?? 0,
                            strike: record.strikeCol.strike,
                            orderDetails: undefined,
                            uuid: uuid(),
                            contractType: 'OPTION',
                            visibility: true
                        })
                        if (basketAnalyzeDetails && basketId !== basketAnalyzeDetails.id) {
                            dispatch(
                                setBasketId({
                                    basketId: basketAnalyzeDetails?.id ?? uuid()
                                })
                            )
                        }
                        getCurentOrderPricesFromData(newOrders, optionsTradesData, underlyingToken, []).then((orderPrices) => {
                            setOrderDataFunction({ orders: newOrders, orderPrices })
                        })
                        if (!basketAnalyzeDetails) commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                    }
                })
            } else {
                const newOrders = [...allOrders]
                const newOrdersPrices = [...allOrdersPrices]
                data.forEach(({type, record, callput})=>{
                    const exist = allOrders.find(v => v.contractType === 'OPTION' && v.data?.key === record?.key && v.type === type && v.callput === callput && v.timeStamp === simulatorTimestamp)
                    if (!exist) {
                        const sameSecDifOrder = newOrders.findIndex(v => v.contractType === 'OPTION' && v.data?.key === record?.key && v.callput === callput && v.timeStamp === simulatorTimestamp)
                        if (sameSecDifOrder >= 0) {
                            if (record) {
                                newOrders[sameSecDifOrder] = {
                                    ...newOrders[sameSecDifOrder],
                                    type,
                                    timeStamp: simulatorTimestamp
                                }
                                newOrdersPrices[sameSecDifOrder] = getCurrentOrderLtpPriceFromData(newOrders[sameSecDifOrder], optionsTradesData ?? [], [])
                                newOrdersPrices[sameSecDifOrder].timeStamp = simulatorTimestamp
                                push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices }}, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                                setSimulatorOrderDataFunction({
                                    orders: newOrders,
                                    orderPrices: newOrdersPrices,
                                })
                            }
                        } else {
                            if (record) {
                                const option = callput === 'call' ? record.callOption : record.putOption
                                newOrders.push({
                                    type,
                                    security: option?.security,
                                    data: record,
                                    callput,
                                    quantity: 1,
                                    expiry: Number(option?.security.expiry?.expiry) ?? 0,
                                    strike: record.strikeCol.strike,
                                    orderDetails: undefined,
                                    uuid: uuid(),
                                    contractType: 'OPTION',
                                    visibility: true,
                                    timeStamp: simulatorTimestamp
                                })
    
                                const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionsTradesData ?? [], [])
                                newPrice.timeStamp = simulatorTimestamp
    
                                newOrdersPrices.push(newPrice)
                                push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices }}, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                                setSimulatorOrderDataFunction({
                                    orders: newOrders,
                                    orderPrices: newOrdersPrices,
                                })
                            }
                        }
                    } else {
                        if (!record) return
                        const option = callput === 'call' ? record.callOption : record.putOption
                        newOrders.push({
                            type,
                            security: option?.security,
                            data: record,
                            callput,
                            quantity: 1,
                            expiry: Number(option?.security.expiry?.expiry) ?? 0,
                            strike: record.strikeCol.strike,
                            orderDetails: undefined,
                            uuid: uuid(),
                            contractType: 'OPTION',
                            visibility: true,
                            timeStamp: simulatorTimestamp
                        })
    
                        const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionsTradesData ?? [], [])
                        newPrice.timeStamp = simulatorTimestamp
    
                        newOrdersPrices.push(newPrice)
                        push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices }}, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                        setSimulatorOrderDataFunction({
                            orders: newOrders,
                            orderPrices: newOrdersPrices,
                        })
                    }
                })
            }
            return
        },
        [optionsTradesData, optionChainViewKey, orderState, allOrders, basketAnalyzeDetails, basketId, underlyingToken, dispatch, simulatorTimestamp, allOrdersPrices, push]
    )

    const customProfit: CustomProfitType | undefined = useMemo(() => {
        if (orders.length > 0 && security && optionsTradesData) {
            const parentSquareOffOrdersPrices: OrderDataPrices[] =
                parentSquareOffOrders.map((o) => {
                    return {
                        price: ((o.orderDetails?.price ?? 0) / (o.security?.priceMultiplier ?? 100)).toFixed(2) ?? '',
                        topAsk: 0,
                        topBid: 0,
                        mid: 0,
                        visibility: o.visibility
                    }
                }) ?? ([] as OrderDataPrices[])

            const dummyData: OiDataTypeBars[] = [
                {
                    strike: undefined,
                    callOi: undefined,
                    putOi: undefined,
                    callDelta: undefined,
                    putDelta: undefined,
                    profit: 0,
                    expectedProfit: 0,
                    duplicateStrike: false
                }
            ]
            let p: number = 0
            const setProfitData = (data: OiDataTypeBars[]) => {
                p = data[0].expectedProfit
            }
            CalculateProfit(
                dummyData,
                updateIvFromAtmIv([...ordersForCalculation, ...parentSquareOffOrders], optionsTradesData, ivMultiplier),
                [...orderPricesForCalculation, ...parentSquareOffOrdersPrices],
                security,
                setProfitData,
                targetDaysFromToday,
                optionChainViewKey===OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp,
                spotPriceForProfitCalc
            )
            return {
                profit: p + profitThreshold,
                chosenSpotPrice: spotPriceForProfitCalc
            }
        }
        return undefined
    }, [orders, ordersForCalculation, security, optionsTradesData, parentSquareOffOrders, ivMultiplier, orderPricesForCalculation, targetDaysFromToday, optionChainViewKey, simulatorTimestamp, spotPriceForProfitCalc, profitThreshold])

    const nonEmptyOrders = useMemo(() => (allOrders.length > 0 || analyzeOrders.length > 0), [allOrders.length, analyzeOrders.length])

    const handleIvMultiplierChange = useCallback((newIvMultiplier: number) => {
        setIvMultiplier(newIvMultiplier)
    }, [])


    const payoffWrapper = useMemo(() => {
        return (
            <Box sx={{ gridArea: 'content' }}>
                <PayoffWrapper
                    spotPrice={basePriceForProfitCalc}
                    data={payoffData}
                    readOnly={readOnly}
                    expandedRows={expandedRows}
                    orders={optionChainViewKey===OptionChainViewKey.LIVE ? analyzePlusAllOrders : simulatorOrders.map(o=>({
                        ...o,
                        readonly: simulatorTimestamp !== o.timeStamp || o.traded
                    }))}
                    showBasketProfit={displayBasketProfit}
                    sdData={sdData}
                    showPlGraph={showPlGraph}
                    breakEvenPoints={breakEven}
                    orderState={orderState}
                    markerPositions={markerPositions}
                    handleStrikeChange={handleStrikeChange}
                    setMarkerPositions={setMarkerPositionsFun}
                    markerPositionsRef={markerPositionsRef}
                    handleAddOrder={handleAddOptionOrder}
                    optionExpiries={optionsTradesData ?? []}
                    orderPrices={ordersPrices}
                    security={security ?? undefined}
                    showSliders
                    historicalOptionChain={optionChainViewKey === OptionChainViewKey.HISTORICAL}
                />
            </Box>
        )
    }, [
        payoffData,
        readOnly,
        expandedRows,
        displayBasketProfit,
        sdData,
        showPlGraph,
        breakEven,
        orderState,
        markerPositions,
        handleStrikeChange,
        handleAddOptionOrder,
        optionsTradesData,
        ordersPrices,
        security,
        analyzePlusAllOrders,
        setMarkerPositionsFun,
        optionChainViewKey,
        simulatorOrders,
        simulatorTimestamp
    ])

    const handleResetParams = useCallback(()=>{
        handleIvMultiplierChange(1)
        setSpotPriceForProfitCalc(price)
        setBasePriceForProfitCalc(price)
        setTargetDaysFromToday(0)
        setPriceThreshold(0)
    }, [handleIvMultiplierChange, price])

    const buttons = useMemo(() => {
        return (nonEmptyOrders && !readOnly) ? (
            <Paper sx={{gridArea: 'pnlbuttons', borderTop: theme => `1px solid ${theme.palette.background.default}`}}>
                <PayoffButtons
                    ivMultiplier={ivMultiplier}
                    handleIvMultiplierChange={handleIvMultiplierChange}
                    setSpotPriceForProfitCalc={setSpotPriceForProfitCalc}
                    price={basePriceForProfitCalc}
                    numberOfDaysToExpire={numberOfDaysToExpire}
                    targetDaysFromToday={targetDaysFromToday}
                    numberOfDaysToExpireArray={numberOfDaysToExpireArray}
                    setTargetDaysFromToday={setTargetDaysFromToday}
                    customProfit={customProfit}
                    handleResetParams={handleResetParams}
                    priceThreshold={priceThreshold}
                    setPriceThreshold={setPriceThreshold}
                    gap={optionChainViewKey===OptionChainViewKey.HISTORICAL ? 2 : undefined}
                    simulator={optionChainViewKey === OptionChainViewKey.HISTORICAL}
                />
            </Paper>
        ) : null
    }, [nonEmptyOrders, readOnly, ivMultiplier, handleIvMultiplierChange, basePriceForProfitCalc, numberOfDaysToExpire, targetDaysFromToday, numberOfDaysToExpireArray, customProfit, handleResetParams, priceThreshold, optionChainViewKey])

    const jsxTree = useMemo(() => {
        return (<Box className={`${classes.container}`} sx={{
            display: 'flex',
            width: '100%',
            flex: 1,
            userSelect: 'none',
            flexDirection: 'column',
            borderRadius: 1
        }}>
            {payoffWrapper}
            {buttons}
        </Box>)
    }, [buttons, payoffWrapper, classes])

    return (jsxTree)
}

export default memo(PlChart)
